/**
 * 환경설정 — 표시·테마 편집 패널
 * 메인색 → soft 추천 → 역할별 슬롯 · 라이트/다크 · ERP 미니 미리보기
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { theme as staticTheme } from 'styles/theme';
import {
  APPEARANCE_SLOT_DEFS,
  APPEARANCE_SLOT_GROUPS,
  defaultAppearanceSettings,
} from 'styles/appearanceSlots';
import {
  softenBrand,
  deriveBrandSlots,
  normalizeHex,
  recommendMainPalettes,
  hexToRgba,
  colorToPickerHex,
  parseRgbaAlpha,
} from 'styles/brandColorUtils';
import { buildThemeFromAppearance } from 'styles/buildThemeFromAppearance';
import { ActionButton } from 'styles/common.styles';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';
import AppearanceTypographyPanel from 'pages/system/AppearanceTypographyPanel';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BlockTitle = styled.h3`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.md};
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
  line-height: 1.45;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ColorInput = styled.input`
  width: 36px;
  height: 28px;
  padding: 0;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.input};
  cursor: pointer;
  flex-shrink: 0;
`;

const HexInput = styled.input`
  width: 110px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.input};
  color: ${(p) => p.theme.colors.text.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
  font-family: ui-monospace, monospace;
`;

const SoftBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: ${staticTheme.borderRadius.sm};
  font-size: ${staticTheme.typography.fontSize.xxs};
  font-weight: 600;
  background: ${(p) => p.theme.colors.primaryLight};
  color: ${(p) => p.theme.colors.primary};
`;

const Chip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 120px;
  padding: 8px 10px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.border)};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => (p.$active ? p.theme.colors.primaryLight : p.theme.colors.background.white)};
  cursor: pointer;
  text-align: left;
`;

const ChipSwatch = styled.div`
  width: 100%;
  height: 18px;
  border-radius: 3px;
  background: ${(p) => p.$color};
`;

const ChipLabel = styled.div`
  font-size: ${staticTheme.typography.fontSize.xs};
  font-weight: 600;
  color: ${(p) => p.theme.colors.text.primary};
`;

const ChipDesc = styled.div`
  font-size: ${staticTheme.typography.fontSize.xxs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const GroupHead = styled.div`
  margin-top: 4px;
  padding: 8px 0 4px;
  border-top: 1px solid ${(p) => p.theme.colors.border};
`;

const GroupTitle = styled.div`
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
`;

const GroupHint = styled.div`
  margin-top: 2px;
  font-size: ${staticTheme.typography.fontSize.xxs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
`;

const SlotCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border: 1px solid ${(p) => (p.$overridden ? p.theme.colors.primary : p.theme.colors.border)};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.white};
`;

const SlotMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SlotName = styled.div`
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 700;
  color: ${(p) => p.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const SlotBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  background: ${(p) => p.theme.colors.primaryLight};
  color: ${(p) => p.theme.colors.primary};
`;

const SlotExample = styled.div`
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.secondary};
  line-height: 1.4;
`;

const SlotHex = styled.div`
  font-size: 10px;
  font-family: ui-monospace, monospace;
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const AlphaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

const AlphaLabel = styled.span`
  font-size: 10px;
  color: ${(p) => p.theme.colors.text.tertiary};
  flex-shrink: 0;
`;

const AlphaRange = styled.input`
  flex: 1;
  min-width: 0;
  accent-color: ${(p) => p.theme.colors.primary};
`;

const AlphaPct = styled.span`
  font-size: 10px;
  font-family: ui-monospace, monospace;
  color: ${(p) => p.theme.colors.text.tertiary};
  width: 32px;
  text-align: right;
`;

const SwatchPreview = styled.div`
  width: 36px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.theme.colors.border};
  flex-shrink: 0;
  background: ${(p) => p.$bg || 'transparent'};
`;

const SlotControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
`;

const ModeToggle = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.medium};
`;

const ModeBtn = styled.button`
  border: none;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: ${staticTheme.typography.fontSize.xs};
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$active ? p.theme.colors.background.white : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.text.secondary)};
  box-shadow: ${(p) => (p.$active ? p.theme.shadows.sm : 'none')};
`;

const Toggle = styled.button`
  position: relative;
  width: 40px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: ${staticTheme.borderRadius.pill};
  background: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.senary)};
  cursor: pointer;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '20px' : '2px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.white};
  }
`;

/* —— 미리보기 (역할 슬롯 전부 노출) —— */
const PreviewShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid ${(p) => p.$border};
  border-radius: ${staticTheme.borderRadius.md};
  background: ${(p) => p.$pageBg};
  font-size: 11px;
  color: ${(p) => p.$text};
`;

const PreviewChrome = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  min-height: 280px;
`;

const PreviewSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 4px;
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
`;

const PreviewMenu = styled.div`
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.$active ? p.$accent : 'transparent')};
  background: ${(p) => (p.$active ? p.$accentSoft : 'transparent')};
  color: ${(p) => (p.$active ? p.$accent : p.$text)};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
`;

const PreviewMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const PreviewTabs = styled.div`
  display: flex;
  gap: 14px;
  border-bottom: 1px solid ${(p) => p.$border};
`;

const PreviewTab = styled.div`
  padding: 4px 2px 6px;
  color: ${(p) => (p.$active ? p.$accent : p.$muted)};
  border-bottom: 2px solid ${(p) => (p.$active ? p.$accent : 'transparent')};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const PreviewBtn = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  min-height: 22px;
  border-radius: 3px;
  border: 1px solid ${(p) => p.$border || p.$bg};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-weight: 600;
  font-size: 10px;
`;

const PreviewField = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 3px;
  border: 2px solid ${(p) => p.$ring};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$text};
  min-width: 88px;
`;

const PreviewInfo = styled.div`
  font-size: 11px;
  color: ${(p) => p.$info};
`;

const PreviewLink = styled.span`
  color: ${(p) => p.$link};
  text-decoration: underline;
  cursor: default;
`;

const PreviewGrid = styled.div`
  border: 1px solid ${(p) => p.$border};
  border-radius: 4px;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr 1fr 72px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-weight: 600;
  border-bottom: 2px solid ${(p) => p.$accent};
`;

const PreviewTh = styled.div`
  padding: 5px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-right: 1px solid ${(p) => p.$divider || 'transparent'};
  &:last-child {
    border-right: none;
  }
`;

const SortMark = styled.span`
  color: ${(p) => p.$color};
  font-size: 9px;
`;

const PreviewRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr 1fr 72px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$text};
  border-top: 1px solid ${(p) => p.$border};
`;

const PreviewTd = styled.div`
  padding: 5px 6px;
  position: relative;
  /* 포커스는 outline — 블럭 테두리와 겹치지 않음 */
  outline: ${(p) => (p.$focused ? `2px solid ${p.$focusColor}` : 'none')};
  outline-offset: -2px;
  background: ${(p) => (p.$selected ? p.$selBg : 'transparent')};
  z-index: ${(p) => (p.$focused || p.$selected ? 1 : 'auto')};
  box-shadow: ${(p) => {
    if (!p.$selected || !p.$blockBorder) return 'none';
    const t = p.$edgeT ? '2px' : '0';
    const b = p.$edgeB ? '2px' : '0';
    const l = p.$edgeL ? '2px' : '0';
    const r = p.$edgeR ? '2px' : '0';
    const c = p.$blockBorder;
    return `
      inset 0 ${t} 0 0 ${c},
      inset 0 -${b} 0 0 ${c},
      inset ${l} 0 0 0 ${c},
      inset -${r} 0 0 0 ${c}
    `;
  }};
`;

const PreviewLegend = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 6px;
  padding-top: 4px;
  border-top: 1px dashed ${(p) => p.$border};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: ${(p) => p.$text};
`;

const LegendSwatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$border};
  flex-shrink: 0;
`;

/**
 * @param {{
 *   value: import('styles/appearanceSlots').AppearanceSettings,
 *   onChange: (next: import('styles/appearanceSlots').AppearanceSettings) => void,
 *   scope?: 'tenant'|'personal',
 * }} props
 */
export default function AppearanceThemePanel({ value, onChange, scope = 'tenant' }) {
  const erpTheme = useErpThemeOptional();
  const [draft, setDraft] = useState(() => ({
    ...defaultAppearanceSettings(),
    ...(value || {}),
  }));

  useEffect(() => {
    setDraft({ ...defaultAppearanceSettings(), ...(value || {}) });
  }, [value]);

  const commit = useCallback(
    (next) => {
      setDraft(next);
      onChange?.(next);
    },
    [onChange]
  );

  const softInfo = useMemo(
    () => softenBrand(draft.sourceHex || draft.mainHex || '#2563EB'),
    [draft.sourceHex, draft.mainHex]
  );

  const autoSlots = useMemo(
    () => deriveBrandSlots(draft.mainHex || softInfo.brandBase),
    [draft.mainHex, softInfo.brandBase]
  );

  const effectiveSlots = useMemo(() => {
    const o = draft.slotOverrides || {};
    return { ...autoSlots, ...o };
  }, [autoSlots, draft.slotOverrides]);

  const previewTheme = useMemo(() => buildThemeFromAppearance(draft), [draft]);

  const palettes = useMemo(
    () => recommendMainPalettes(draft.sourceHex || draft.mainHex || '#2563EB'),
    [draft.sourceHex, draft.mainHex]
  );

  const setMainFromSource = useCallback(
    (hex) => {
      const n = normalizeHex(hex) || hex;
      const soft = softenBrand(n);
      commit({
        ...draft,
        sourceHex: soft.sourceHex,
        mainHex: soft.brandBase,
        softened: soft.softened,
        slotOverrides: {},
      });
    },
    [commit, draft]
  );

  const setSlot = useCallback(
    (id, hex, slotDef) => {
      const n = normalizeHex(hex) || hex;
      let value = n;
      if (slotDef?.storeAsRgba) {
        const prev = draft.slotOverrides?.[id] || autoSlots[id];
        const alpha =
          parseRgbaAlpha(prev) ??
          (typeof slotDef.rgbaAlpha === 'number' ? slotDef.rgbaAlpha : 0.28);
        value = hexToRgba(n, alpha);
      }
      commit({
        ...draft,
        slotOverrides: { ...(draft.slotOverrides || {}), [id]: value },
      });
    },
    [commit, draft, autoSlots]
  );

  /** rgba 슬롯 투명도만 변경 (색 RGB 유지) */
  const setSlotAlpha = useCallback(
    (id, alpha, slotDef) => {
      const prev = draft.slotOverrides?.[id] || autoSlots[id];
      const hex = colorToPickerHex(prev);
      const a = Math.min(1, Math.max(0.05, Number(alpha) || slotDef?.rgbaAlpha || 0.28));
      commit({
        ...draft,
        slotOverrides: { ...(draft.slotOverrides || {}), [id]: hexToRgba(hex, a) },
      });
    },
    [commit, draft, autoSlots]
  );

  const resetSlot = useCallback(
    (id) => {
      const next = { ...(draft.slotOverrides || {}) };
      delete next[id];
      commit({ ...draft, slotOverrides: next });
    },
    [commit, draft]
  );

  const resetAllSlots = useCallback(() => {
    commit({ ...draft, slotOverrides: {} });
  }, [commit, draft]);

  const applyLive = useCallback(() => {
    if (!erpTheme) return;
    if (scope === 'personal') {
      erpTheme.updatePersonalAppearance(draft);
    } else {
      erpTheme.updateTenantAppearance(draft);
    }
  }, [erpTheme, draft, scope]);

  const c = previewTheme.colors;
  const s = effectiveSlots;

  return (
    <Wrap>
      <Block>
        <BlockTitle>표시 모드</BlockTitle>
        <Hint>라이트/다크는 개인 설정으로도 바꿀 수 있습니다. 기관 기본값은 아래에서 저장됩니다.</Hint>
        <ModeToggle>
          <ModeBtn
            type="button"
            $active={draft.colorMode !== 'dark'}
            onClick={() => commit({ ...draft, colorMode: 'light' })}
          >
            라이트
          </ModeBtn>
          <ModeBtn
            type="button"
            $active={draft.colorMode === 'dark'}
            onClick={() => commit({ ...draft, colorMode: 'dark' })}
          >
            다크
          </ModeBtn>
        </ModeToggle>
      </Block>

      <AppearanceTypographyPanel
        draft={draft}
        saved={value}
        onChange={commit}
        scope={scope}
      />

      <Block>
        <BlockTitle>메인 색상 (CI)</BlockTitle>
        <Hint>
          기관 CI 메인색을 넣으면 눈 피로가 적은 톤으로 완화한 뒤, 아래 역할 색이 자동으로 맞춰집니다.
        </Hint>
        <Row>
          <ColorInput
            type="color"
            value={normalizeHex(draft.sourceHex || draft.mainHex) || '#2563EB'}
            onChange={(e) => setMainFromSource(e.target.value)}
            title="원본 CI 색"
          />
          <HexInput
            value={draft.sourceHex || draft.mainHex || ''}
            onChange={(e) => commit({ ...draft, sourceHex: e.target.value })}
            onBlur={(e) => {
              const n = normalizeHex(e.target.value);
              if (n) setMainFromSource(n);
            }}
            placeholder="#2563EB"
          />
          <SoftBadge title="완화 적용 결과">
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: draft.mainHex || softInfo.brandBase,
                display: 'inline-block',
              }}
            />
            적용 {draft.mainHex || softInfo.brandBase}
            {draft.softened || softInfo.softened ? ' · 완화됨' : ''}
          </SoftBadge>
          <ActionButton type="button" onClick={() => setMainFromSource(draft.sourceHex || draft.mainHex)}>
            완화 재적용
          </ActionButton>
        </Row>
      </Block>

      <Block>
        <BlockTitle>추천 배색</BlockTitle>
        <Row>
          {palettes.map((p) => (
            <Chip
              key={p.id}
              type="button"
              $active={(draft.mainHex || '').toUpperCase() === p.mainHex.toUpperCase()}
              onClick={() =>
                commit({
                  ...draft,
                  mainHex: p.mainHex,
                  softened: true,
                  slotOverrides: {},
                })
              }
            >
              <ChipSwatch $color={p.mainHex} />
              <ChipLabel>{p.label}</ChipLabel>
              <ChipDesc>{p.description}</ChipDesc>
            </Chip>
          ))}
        </Row>
      </Block>

      <Block>
        <BlockTitle>역할별 색상 (자동 + 수동)</BlockTitle>
        <Hint>
          메인색에서 자동으로 채워집니다. 화면에서 어디에 쓰이는지 보고 필요한 항목만 색을 바꾸세요. 「리셋」하면
          그 항목만 자동값으로 돌아갑니다.
        </Hint>
        <Row>
          <ActionButton type="button" onClick={resetAllSlots}>
            슬롯 전부 자동으로
          </ActionButton>
        </Row>

        {APPEARANCE_SLOT_GROUPS.map((grp) => (
          <div key={grp.id}>
            <GroupHead>
              <GroupTitle>{grp.label}</GroupTitle>
              <GroupHint>{grp.hint}</GroupHint>
            </GroupHead>
            <SlotGrid>
              {APPEARANCE_SLOT_DEFS.filter((slot) => slot.group === grp.id).map((slot) => {
                const auto = autoSlots[slot.id];
                const cur = effectiveSlots[slot.id] || auto;
                const overridden = !!(draft.slotOverrides && draft.slotOverrides[slot.id]);
                const pickerHex = colorToPickerHex(cur);
                const alpha =
                  parseRgbaAlpha(cur) ??
                  (typeof slot.rgbaAlpha === 'number' ? slot.rgbaAlpha : 0.28);
                return (
                  <SlotCard key={slot.id} $overridden={overridden}>
                    <SlotControls>
                      <ColorInput
                        type="color"
                        value={pickerHex}
                        onChange={(e) => setSlot(slot.id, e.target.value, slot)}
                        title={`${slot.label} 색 선택`}
                      />
                      {slot.storeAsRgba ? (
                        <SwatchPreview $bg={cur} title="반투명 적용 미리보기" />
                      ) : null}
                    </SlotControls>
                    <SlotMeta>
                      <SlotName>
                        {slot.label}
                        {overridden ? <SlotBadge>수동</SlotBadge> : null}
                      </SlotName>
                      <SlotExample>{slot.example}</SlotExample>
                      <SlotHex>{cur}</SlotHex>
                      {slot.storeAsRgba ? (
                        <AlphaRow>
                          <AlphaLabel>투명도</AlphaLabel>
                          <AlphaRange
                            type="range"
                            min={8}
                            max={90}
                            step={1}
                            value={Math.round(alpha * 100)}
                            onChange={(e) =>
                              setSlotAlpha(slot.id, Number(e.target.value) / 100, slot)
                            }
                            title="셀 선택 하이라이트 투명도"
                          />
                          <AlphaPct>{Math.round(alpha * 100)}%</AlphaPct>
                        </AlphaRow>
                      ) : null}
                    </SlotMeta>
                    {overridden ? (
                      <ActionButton type="button" onClick={() => resetSlot(slot.id)}>
                        리셋
                      </ActionButton>
                    ) : null}
                  </SlotCard>
                );
              })}
            </SlotGrid>
          </div>
        ))}
      </Block>

      {scope === 'tenant' ? (
        <Block>
          <BlockTitle>개인 브랜드 색 허용</BlockTitle>
          <Hint>
            켜면 사용자가 기관 메인색·슬롯을 개인 설정으로 덮어쓸 수 있습니다. 표시 모드(라이트/다크)는 항상 개인
            변경 가능합니다.
          </Hint>
          <Row>
            <Toggle
              type="button"
              $on={!!draft.allowPersonalBrand}
              aria-pressed={!!draft.allowPersonalBrand}
              onClick={() => commit({ ...draft, allowPersonalBrand: !draft.allowPersonalBrand })}
            />
            <span style={{ fontSize: 12 }}>{draft.allowPersonalBrand ? '허용' : '기관 브랜드 고정'}</span>
          </Row>
        </Block>
      ) : null}

      <Block>
        <BlockTitle>미리보기 — 역할 색 한눈에</BlockTitle>
        <Hint>아래 미니 ERP 화면과 범례에 슬롯이 모두 연결되어 있습니다. 색을 바꾸면 즉시 반영됩니다.</Hint>

        <PreviewShell $pageBg={c.background.page} $border={c.border} $text={c.text.primary}>
          <PreviewChrome>
            <PreviewSide $bg={c.background.sidebar} $border={c.border}>
              <PreviewMenu $text={c.text.secondary} $accent={s.primary} $accentSoft={s.primaryLight}>
                인사관리
              </PreviewMenu>
              <PreviewMenu
                $active
                $text={c.text.secondary}
                $accent={s.primary}
                $accentSoft={s.primaryLight}
              >
                예산관리
              </PreviewMenu>
              <PreviewMenu $text={c.text.secondary} $accent={s.primary} $accentSoft={s.primaryLight}>
                기준정보
              </PreviewMenu>
            </PreviewSide>

            <PreviewMain>
              <PreviewTabs $border={c.border}>
                <PreviewTab $active $accent={s.primary} $muted={c.text.tertiary}>
                  편성요구
                </PreviewTab>
                <PreviewTab $accent={s.primaryDark} $muted={c.text.tertiary}>
                  과목등록
                </PreviewTab>
              </PreviewTabs>

              <PreviewToolbar>
                <PreviewBtn $bg={s.primary} $fg="#fff" $border={s.primary}>
                  조회
                </PreviewBtn>
                <PreviewBtn $bg={s.primaryDark} $fg="#fff" $border={s.primaryDark}>
                  저장
                </PreviewBtn>
                <PreviewBtn $bg={c.background.white} $fg={c.text.secondary} $border={c.border}>
                  엑셀
                </PreviewBtn>
                <PreviewField $ring={s.focusRing} $bg={c.background.input} $text={c.text.primary}>
                  회계연도
                </PreviewField>
                <PreviewInfo $info={s.statusInfo}>
                  안내: 조회 후 편집하세요 ·{' '}
                  <PreviewLink $link={s.textLink}>도움말 링크</PreviewLink>
                </PreviewInfo>
              </PreviewToolbar>

              {/* 활성 헤더 + 정렬·hover·액센트 · 선택/hover 행 · 셀 포커스 */}
              <PreviewGrid $border={c.grid.border}>
                <PreviewHeader
                  $bg={s.gridHeaderActive}
                  $fg={s.gridHeaderActiveFg}
                  $accent={s.gridHeaderAccent}
                >
                  <PreviewTh $divider="rgba(255,255,255,0.2)">No</PreviewTh>
                  <PreviewTh $divider="rgba(255,255,255,0.2)">
                    과목
                    <SortMark $color={s.gridHeaderActiveSort}>▲</SortMark>
                  </PreviewTh>
                  <PreviewTh
                    $divider="rgba(255,255,255,0.2)"
                    style={{ background: s.gridHeaderActiveHover }}
                    title="활성 헤더 hover"
                  >
                    금액
                  </PreviewTh>
                  <PreviewTh>비고</PreviewTh>
                </PreviewHeader>
                <PreviewRow $bg={s.gridRowSelected} $text={c.text.primary} $border={c.grid.border}>
                  <PreviewTd>1</PreviewTd>
                  <PreviewTd
                    $focused
                    $focusColor={s.gridCellFocused}
                    $selected
                    $selBg={s.gridCellSelected}
                    $blockBorder={s.gridCellBlockBorder}
                    $edgeT
                    $edgeL
                  >
                    인건비
                  </PreviewTd>
                  <PreviewTd
                    $selected
                    $selBg={s.gridCellSelected}
                    $blockBorder={s.gridCellBlockBorder}
                    $edgeT
                    $edgeR
                  >
                    1,200
                  </PreviewTd>
                  <PreviewTd>
                    <PreviewLink $link={s.textLink}>상세</PreviewLink>
                  </PreviewTd>
                </PreviewRow>
                <PreviewRow $bg={s.gridRowHover} $text={c.text.primary} $border={c.grid.border}>
                  <PreviewTd>2</PreviewTd>
                  <PreviewTd
                    $selected
                    $selBg={s.gridCellSelected}
                    $blockBorder={s.gridCellBlockBorder}
                    $edgeB
                    $edgeL
                  >
                    운영비
                  </PreviewTd>
                  <PreviewTd
                    $selected
                    $selBg={s.gridCellSelected}
                    $blockBorder={s.gridCellBlockBorder}
                    $edgeB
                    $edgeR
                  >
                    800
                  </PreviewTd>
                  <PreviewTd />
                </PreviewRow>
                <PreviewRow $bg={c.white} $text={c.text.primary} $border={c.grid.border}>
                  <PreviewTd>3</PreviewTd>
                  <PreviewTd>여비</PreviewTd>
                  <PreviewTd>120</PreviewTd>
                  <PreviewTd />
                </PreviewRow>
              </PreviewGrid>
            </PreviewMain>
          </PreviewChrome>

          <PreviewLegend $border={c.border}>
            {APPEARANCE_SLOT_DEFS.map((slot) => (
              <LegendItem key={slot.id} $text={c.text.secondary}>
                <LegendSwatch $color={s[slot.id] || autoSlots[slot.id]} $border={c.border} />
                {slot.label}
              </LegendItem>
            ))}
          </PreviewLegend>
        </PreviewShell>

        <Row>
          <ActionButton type="button" $variant="primary" onClick={applyLive}>
            지금 화면에 미리 적용
          </ActionButton>
          <Hint>저장 버튼으로 영속화됩니다. 미리 적용은 즉시 ThemeProvider에 반영합니다.</Hint>
        </Row>
      </Block>
    </Wrap>
  );
}
